'use client';

import { useState, useEffect, useCallback } from 'react';
import { Clipboard, Copy, RefreshCw, Moon, Sun, Save, BarChart2, Clock, Type, Hash, AlignLeft, AlertCircle, Check } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { wordCounterContent } from '../../data/word-counter-content';

type TextCase = 'original' | 'upper' | 'lower' | 'title' | 'sentence';

interface TextStats {
    words: number;
    characters: number;
    charactersNoSpaces: number;
    sentences: number;
    paragraphs: number;
    readingTime: number;
    speakingTime: number;
    keywordDensity: { [key: string]: number };
    mostUsedWords: [string, number][];
    averageWordLength: number;
    characterFrequency: { [key: string]: number };
    wordsPerSentence: number;
    sentencesPerParagraph: number;
}

export default function WordCounter() {
    const [text, setText] = useState('');
    const [stats, setStats] = useState<TextStats | null>(null);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [textCase, setTextCase] = useState<TextCase>('original');
    const [copied, setCopied] = useState(false);
    const [saved, setSaved] = useState(false);

    // Toggle dark mode
    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [isDarkMode]);

    // Load saved text from localStorage
    useEffect(() => {
        const savedText = localStorage.getItem('savedText');
        if (savedText) {
            setText(savedText);
        }
    }, []);

    // Calculate text statistics
    const calculateStats = useCallback((content: string): TextStats => {
        if (!content.trim()) {
            return {
                words: 0,
                characters: 0,
                charactersNoSpaces: 0,
                sentences: 0,
                paragraphs: 0,
                readingTime: 0,
                speakingTime: 0,
                keywordDensity: {},
                mostUsedWords: [],
                averageWordLength: 0,
                characterFrequency: {},
                wordsPerSentence: 0,
                sentencesPerParagraph: 0
            };
        }

        // Basic counts
        const words = content.trim() === '' ? 0 : content.trim().split(/\s+/).length;
        const characters = content.length;
        const charactersNoSpaces = content.replace(/\s+/g, '').length;
        const sentences = content.split(/[.!?]+/).filter(Boolean).length;
        const paragraphs = content.split(/\n\s*\n/).filter(Boolean).length;

        // Reading time (average reading speed: 200 words per minute)
        const readingTime = Math.ceil(words / 200);

        // Speaking time (average speaking speed: 150 words per minute)
        const speakingTime = Math.ceil(words / 150);

        // Word frequency and keyword density
        const wordFrequency: { [key: string]: number } = {};
        const wordsArray = content
            .toLowerCase()
            .replace(/[^\w\s]|_/g, '')
            .split(/\s+/)
            .filter(word => word.length > 0);

        wordsArray.forEach(word => {
            wordFrequency[word] = (wordFrequency[word] || 0) + 1;
        });

        // Calculate keyword density (percentage of total words)
        const keywordDensity: { [key: string]: number } = {};
        Object.entries(wordFrequency).forEach(([word, count]) => {
            keywordDensity[word] = parseFloat(((count / words) * 100).toFixed(2));
        });

        // Get most used words (top 10)
        const mostUsedWords = Object.entries(wordFrequency)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10);

        // Calculate average word length
        const totalLetters = wordsArray.join('').length;
        const averageWordLength = parseFloat((totalLetters / words).toFixed(2));

        // Character frequency
        const characterFrequency: { [key: string]: number } = {};
        const chars = content.toLowerCase().replace(/\s+/g, '').split('');
        chars.forEach(char => {
            characterFrequency[char] = (characterFrequency[char] || 0) + 1;
        });

        // Words per sentence
        const sentenceArray = content.split(/[.!?]+/).filter(Boolean);
        const wordsPerSentence = parseFloat((words / (sentenceArray.length || 1)).toFixed(2));

        // Sentences per paragraph
        const paragraphArray = content.split(/\n\s*\n/).filter(Boolean);
        const sentencesPerParagraph = parseFloat((sentences / (paragraphArray.length || 1)).toFixed(2));

        return {
            words,
            characters,
            charactersNoSpaces,
            sentences,
            paragraphs,
            readingTime,
            speakingTime,
            keywordDensity,
            mostUsedWords,
            averageWordLength,
            characterFrequency,
            wordsPerSentence,
            sentencesPerParagraph
        };
    }, []);

    // Update stats when text changes
    useEffect(() => {
        setStats(calculateStats(text));
    }, [text, calculateStats]);

    // Handle text case changes
    const handleCaseChange = (newCase: TextCase) => {
        setTextCase(newCase);
        switch (newCase) {
            case 'upper':
                setText(prev => prev.toUpperCase());
                break;
            case 'lower':
                setText(prev => prev.toLowerCase());
                break;
            case 'title':
                setText(prev =>
                    prev.toLowerCase()
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ')
                );
                break;
            case 'sentence':
                setText(prev =>
                    prev.toLowerCase()
                        .split('. ')
                        .map(sentence =>
                            sentence.trim() === '' ? '' :
                                sentence.charAt(0).toUpperCase() + sentence.slice(1)
                        )
                        .join('. ')
                );
                break;
            default:
                break;
        }
    };

    // Copy text to clipboard
    const copyToClipboard = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Save text to localStorage
    const saveText = () => {
        localStorage.setItem('savedText', text);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
    };

    // Clear all text
    const clearText = () => {
        if (confirm('Are you sure you want to clear all text?')) {
            setText('');
            localStorage.removeItem('savedText');
        }
    };

    return (
        <div className={`min-h-screen p-6 md:p-8 ${isDarkMode ? 'dark bg-gray-900 text-white' : 'bg-gray-50'}`}>
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
                    <div>
                        <ToolPageIntro title="Word Counter - Free Online Text Analysis Tool">
                Count words, characters, sentences, and analyze text with our free word counter. Perfect for writers, students, and content creators.
            </ToolPageIntro>
                    </div>
                    <div className="flex items-center space-x-2 mt-4 md:mt-0">
                        <button
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className="px-4 py-2 rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors flex items-center"
                        >
                            <BarChart2 className="w-4 h-4 mr-2" />
                            {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
                        </button>
                        <button
                            onClick={() => setIsDarkMode(!isDarkMode)}
                            className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                            aria-label="Toggle dark mode"
                        >
                            {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Text Input */}
                    <div className="lg:col-span-2">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 h-full">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold">Your Text</h2>
                                <div className="flex space-x-2">
                                    <select
                                        value={textCase}
                                        onChange={(e) => handleCaseChange(e.target.value as TextCase)}
                                        className="text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1"
                                    >
                                        <option value="original">Original Case</option>
                                        <option value="upper">UPPERCASE</option>
                                        <option value="lower">lowercase</option>
                                        <option value="title">Title Case</option>
                                        <option value="sentence">Sentence case</option>
                                    </select>
                                    <button
                                        onClick={clearText}
                                        className="p-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                        title="Clear all text"
                                    >
                                        <RefreshCw className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={saveText}
                                        className="p-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                        title="Save text"
                                    >
                                        <Save className={`w-4 h-4 ${saved ? 'text-green-500' : ''}`} />
                                    </button>
                                    <button
                                        onClick={copyToClipboard}
                                        className="p-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                        title="Copy to clipboard"
                                    >
                                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Clipboard className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="Type or paste your text here..."
                                className="w-full h-96 p-4 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                            />

                            {/* Basic Stats */}
                            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                                    <div className="text-gray-600 dark:text-gray-300">Words</div>
                                    <div className="text-2xl font-bold">{stats?.words || 0}</div>
                                </div>
                                <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                                    <div className="text-gray-600 dark:text-gray-300">Characters</div>
                                    <div className="text-2xl font-bold">{stats?.characters || 0}</div>
                                </div>
                                <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                                    <div className="text-gray-600 dark:text-gray-300">Sentences</div>
                                    <div className="text-2xl font-bold">{stats?.sentences || 0}</div>
                                </div>
                                <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                                    <div className="text-gray-600 dark:text-gray-300">Paragraphs</div>
                                    <div className="text-2xl font-bold">{stats?.paragraphs || 0}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Stats Sidebar */}
                    <div className="space-y-6">
                        {/* Reading Time */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
                            <h3 className="text-lg font-semibold mb-3 flex items-center">
                                <Clock className="w-5 h-5 mr-2 text-blue-500" />
                                Reading & Speaking Time
                            </h3>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Reading Time:</span>
                                    <span className="font-medium">{stats?.readingTime || 0} min</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Speaking Time:</span>
                                    <span className="font-medium">{stats?.speakingTime || 0} min</span>
                                </div>
                            </div>
                        </div>

                        {/* Character Count */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
                            <h3 className="text-lg font-semibold mb-3 flex items-center">
                                <Type className="w-5 h-5 mr-2 text-green-500" />
                                Character Count
                            </h3>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Characters (with spaces):</span>
                                    <span className="font-medium">{stats?.characters || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Characters (no spaces):</span>
                                    <span className="font-medium">{stats?.charactersNoSpaces || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Average Word Length:</span>
                                    <span className="font-medium">{stats?.averageWordLength || 0} chars</span>
                                </div>
                            </div>
                        </div>

                        {/* Advanced Stats - Conditionally Rendered */}
                        {showAdvanced && (
                            <>
                                {/* Word Density */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
                                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                                        <BarChart2 className="w-5 h-5 mr-2 text-purple-500" />
                                        Word Density
                                    </h3>
                                    <div className="space-y-2 max-h-60 overflow-y-auto">
                                        {stats?.mostUsedWords.map(([word, count]) => (
                                            <div key={word} className="flex justify-between">
                                                <span className="text-gray-600 dark:text-gray-300">{word}:</span>
                                                <div className="flex items-center">
                                                    <span className="font-medium mr-2">{count}</span>
                                                    <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                                        <div
                                                            className="bg-purple-500 h-2 rounded-full"
                                                            style={{ width: `${(count / (stats?.mostUsedWords[0]?.[1] || 1)) * 100}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Text Statistics */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
                                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                                        <Hash className="w-5 h-5 mr-2 text-yellow-500" />
                                        Text Statistics
                                    </h3>
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-300">Words per Sentence:</span>
                                            <span className="font-medium">{stats?.wordsPerSentence || 0}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-300">Sentences per Paragraph:</span>
                                            <span className="font-medium">{stats?.sentencesPerParagraph || 0}</span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Tips */}
                        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-md">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <AlertCircle className="h-5 w-5 text-blue-400" />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Writing Tips</h3>
                                    <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                                        <ul className="list-disc pl-5 space-y-1">
                                            <li>Aim for 15-20 words per sentence for readability</li>
                                            <li>Keep paragraphs under 6 sentences</li>
                                            <li>Use active voice for clearer writing</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Informational Content */}
                <div className="mt-12">
                    <InformationalContent
                        title={wordCounterContent.title}
                        description={wordCounterContent.description}
                        content={wordCounterContent.content}
                        faqs={wordCounterContent.faqs}
                        relatedTools={wordCounterContent.relatedTools}
                    />
                </div>
            </div>
        </div>
    );
}
